// src/components/Employees/FieldSelector.js

import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./FieldSelector.module.css";

const FieldSelector = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the fields, initial selection, and return path from the navigation state
  const {
    allFields = [],
    initialSelectedIds = [],
    returnPath = "/",
    preservedState,
  } = location.state || {};
  console.log("all fields", allFields);

  const [currentSelection, setCurrentSelection] = useState(initialSelectedIds);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredFields = useMemo(
    () =>
      allFields.filter((field) =>
        field.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [allFields, searchTerm]
  );

  const handleSelectAll = () => {
    const filteredIds = filteredFields.map((f) => f.id);
    const allFilteredSelected =
      filteredIds.length > 0 &&
      filteredIds.every((id) => currentSelection.includes(id));

    if (allFilteredSelected) {
      // Deselect all filtered items
      setCurrentSelection(
        currentSelection.filter((id) => !filteredIds.includes(id))
      );
    } else {
      // Select all filtered items
      setCurrentSelection([...new Set([...currentSelection, ...filteredIds])]);
    }
  };

  const handleSelect = (fieldId, isChecked) => {
    setCurrentSelection(
      isChecked
        ? [...currentSelection, fieldId]
        : currentSelection.filter((id) => id !== fieldId)
    );
  };

  const handleConfirm = () => {
    // Navigate back, passing the updated selection in the state
    navigate(returnPath, {
      state: {
        preservedState,
        fieldSelection: currentSelection,
      },
    });
  };

  const handleGoBack = () => {
    // Go back without saving changes
    navigate(returnPath, {
      state: {
        preservedState,
        fieldSelection: initialSelectedIds,
      },
    });
  };

  const allFilteredSelected =
    filteredFields.length > 0 &&
    filteredFields.every((f) => currentSelection.includes(f.id));

  return (
    <div className={styles.pageContainer}>
      <header className={styles.pageHeader}>
        <button onClick={handleGoBack} className={styles.backButton}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M10.8284 12.0007L15.7782 16.9504L14.364 18.3646L8 12.0007L14.364 5.63672L15.7782 7.05093L10.8284 12.0007Z"></path>
          </svg>
        </button>
        <h1>{t("Select Sector")}</h1>
        <div className={styles.headerPlaceholder}></div>{" "}
        {/* To center the title */}
      </header>

      <main className={styles.pageContent}>
        <div className={styles.searchWrapper}>
          <svg
            className={styles.searchIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t("Search by name...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className={styles.selectAllControl}>
          <button
            type="button"
            className={styles.selectAllButton}
            onClick={handleSelectAll}
          >
            {allFilteredSelected ? t("Deselect All") : t("Select All")}
          </button>
        </div>

        <div className={styles.selectorListItems}>
          {filteredFields.length > 0 ? (
            filteredFields.map((field) => {
              const isSelected = currentSelection.includes(field.id);
              return (
                <label key={field.id} className={styles.selectorItemRow}>
                  <div className={styles.itemDetails}>
                    <span className={styles.itemName}>{field.name}</span>
                    <span className={styles.itemArea}>
                      {field.area && field.areaUnit
                        ? `${field.area} ${t(field.areaUnit)}`
                        : ""}
                    </span>
                  </div>
                  <div className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleSelect(field.id, e.target.checked)}
                    />
                    <span className={styles.customCheckbox}></span>
                  </div>
                </label>
              );
            })
          ) : (
            <div className={styles.noResults}>
              <p>{t("No sectors found.")}</p>
            </div>
          )}
        </div>
      </main>

      <footer className={styles.pageFooter}>
        <button className={styles.confirmButton} onClick={handleConfirm}>
          {t("Continue")} ({currentSelection.length})
        </button>
      </footer>
    </div>
  );
};

export default FieldSelector;
